// Contact.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function Contact() {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [toast, setToast] = useState({ message: "", type: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const backgroundImage = isMobile
        ? "https://geneartai.com/images/mobile1.png"
        : "https://geneartai.com/images/image1.png";

    const handleChange = (e) =>
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setToast({ message: "", type: "" });

        if (!form.name || !form.email) {
            setToast({ message: "Name and Email are required.", type: "error" });
            return;
        }

        setLoading(true);
        try {
            const res = await fetch("/api/send-contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (res.ok) {
                setToast({ message: "Message sent successfully!", type: "success" });
                setForm({ name: "", email: "", message: "" });
            } else {
                throw new Error(data.error || "Failed to send message");
            }
        } catch (err) {
            setToast({ message: err.message, type: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen font-sans text-white overflow-x-hidden">
            {/* Background */}
            <div
                className="fixed inset-0 bg-cover bg-center z-0"
                style={{
                    backgroundImage: `url('${backgroundImage}')`,
                    backgroundAttachment: isMobile ? "scroll" : "fixed",
                }}
            />
            <div className="absolute inset-0 bg-black/20" />

            {/* Foreground Content */}
            <div className="relative z-10 flex flex-col min-h-screen pt-28">
                <div className="max-w-3xl mx-auto p-6 rounded-2xl bg-black/20 backdrop-blur-sm shadow-lg">
                    <h1 className="text-3xl font-bold mb-4 text-center">Contact Us</h1>
                    <p className="text-gray-300 text-center mb-6">
                        We'd love to hear from you! Fill out the form below and we'll get back to you shortly.
                    </p>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Your Name"
                            className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white"
                        />
                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="Your Email"
                            className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white"
                        />
                        <textarea
                            name="message"
                            rows={5}
                            value={form.message}
                            onChange={handleChange}
                            placeholder="Your Message"
                            className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-2 bg-blue-800 hover:bg-[#166CB0] text-white font-bold rounded-full"
                        >
                            {loading ? "Sending..." : "Send Message"}
                        </button>
                        {toast.message && (
                            <div
                                className={`text-center text-sm mt-2 font-semibold ${toast.type === "error" ? "text-red-400" : "text-green-400"
                                    }`}
                            >
                                {toast.message}
                            </div>
                        )}
                    </form>
                </div>

                {/* Footer */}
                <footer className="text-center text-xs text-gray-300 py-6 mt-auto">
                    <div>© 2025 GeneArtAI — All Rights Reserved.</div>
                    <div className="mt-2">
                        <Link to="/privacy-policy" className="text-gray-300 hover:text-white underline">
                            Privacy Policy
                        </Link>{" "}
                        |{" "}
                        <Link to="/terms-of-service" className="underline hover:text-white">
                            Terms of Service
                        </Link>
                    </div>
                </footer>
            </div>
        </div>
    );
}
